import { useMemo } from 'react';
import { WithAccountId } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { TableHeader, useTableState } from '@optimus-web/core';
import { PaymentRequest, useRepositoryQuery } from '@mews-commander/data-access';
import { TableDivider } from '../TableDivider';
import { PaymentTable } from '../PaymentTable';
import { getPaymentRequestsColumns } from './PaymentRequestsTableMapping';
import { PaymentRequestsTableActionButton } from './PaymentRequestsTableActionButton';
import { useGetAllPaymentRequests } from './hooks';

export const PaymentRequestsTable = ({
    accountId,
    hideDivider = false,
    isCompany = false,
}: { hideDivider?: boolean; isCompany?: boolean } & WithAccountId) => {
    const [tableState, setTableState] = useTableState<PaymentRequest>({
        pagination: {
            show: false,
        },
    });
    const paymentRequestRecordsQuery = useGetAllPaymentRequests({ accountId });
    const paymentRequestRecords = paymentRequestRecordsQuery.data ?? [];

    const methodsAvailabilityQuery = useRepositoryQuery('paymentMethods', 'getAvailability', { accountId });

    const columns = useMemo(() => getPaymentRequestsColumns({ accountId, isCompany }), [accountId, isCompany]);

    return (
        <>
            <TableHeader
                title={<LocalizedText textKey={TextKey.PaymentRequestPlural} />}
                rightSide={
                    <PaymentRequestsTableActionButton
                        accountId={accountId}
                        disabled={!methodsAvailabilityQuery.data?.paymentRequest}
                    />
                }
            />
            <PaymentTable
                columns={columns}
                data={paymentRequestRecords}
                uniqueRowId="id"
                state={tableState}
                onStateChange={setTableState}
                amountOfRows={paymentRequestRecords.length}
                loading={paymentRequestRecordsQuery.isFetching || methodsAvailabilityQuery.isFetching}
                isError={paymentRequestRecordsQuery.isError || methodsAvailabilityQuery.isError}
                onTryAgain={() => {
                    void paymentRequestRecordsQuery.refetch();
                    void methodsAvailabilityQuery.refetch();
                }}
            />
            {!hideDivider && <TableDivider />}
        </>
    );
};
